import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState, } from 'react';
import { styled } from '@mui/material/styles';
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment, Radio, RadioGroup, TextField, Tooltip, Typography, } from '@mui/material';
import { ArrowDropDown, Close } from '@mui/icons-material';
import { MaterialDropdown, TempstepField } from 'console/shared/components/fields';
import { ControlWithoutLabel } from 'console/shared/components/fields/control/without-label';
import { preventDefault } from 'shared/hooks/utils';
import { calcTemp2Color } from './type';
import { HeatingMode } from 'z3k-config';
import { prettyFloat } from 'utils';
import { getTempOrMode } from './transform-schedule';
import { useBooleanState } from 'shared/hooks/state/boolean';
const PREFIX = 'TemperatureInput';
const classes = {
    button_to_right: `${PREFIX}-button_to_right`,
};
// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
    [`& .${classes.button_to_right}`]: {
        marginRight: -20,
    },
});
function __range__(left, right, inclusive) {
    const range = [];
    const ascending = left < right;
    const end = !inclusive ? right : ascending ? right + 1 : right - 1;
    for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--)
        range.push(i);
    return range;
}
const TemperaturePicker = memo(function TemperaturePicker(props) {
    const { onChange } = props;
    const onClick = useCallback((temp) => () => onChange(temp), [onChange]);
    const range = useMemo(() => __range__(5, 40, true), []);
    return (_jsx(Box, { display: "flex", flexWrap: "wrap", maxWidth: "100%", ml: 2, style: { gap: 5 }, children: range.map((temp) => (_jsx(Box, { children: _jsx(Tooltip, { title: temp + ' °C', children: _jsx(IconButton, { onClick: onClick(temp), size: "small", children: _jsx(Avatar, { style: {
                            backgroundColor: calcTemp2Color(temp),
                        }, children: temp }) }) }) }, temp))) }));
});
export const TemperatureInput = memo(function TemperatureInput(props) {
    const { heating_modes, onChange, disabled, is_boiler_mode } = props;
    const [temp_or_mode, setTempOrMode] = useState(props.temp_or_mode);
    const [radio_mode_or_temp, setRadioModeOrTemp] = useState(temp_or_mode.mode != null ? 'mode' : 'temp');
    const [selected_hm_id, setSelectedHmId] = useState(props.temp_or_mode.mode?.id
        ? props.temp_or_mode.mode?.id
        : heating_modes[0]
            ? heating_modes[0].id
            : undefined);
    const [, setBoilerSelectedValue] = useState(props.temp_or_mode.temp != undefined
        ? props.temp_or_mode.temp
        : props.temp_or_mode.mode?.id
            ? props.temp_or_mode.mode?.id
            : heating_modes[0]?.id ?? 0);
    // учитываем внешние изменения
    useEffect(() => {
        setTempOrMode(props.temp_or_mode);
        if (props.temp_or_mode.mode != null) {
            setRadioModeOrTemp('mode');
            setSelectedHmId(props.temp_or_mode.mode.id);
        }
        else {
            setRadioModeOrTemp('temp');
        }
    }, [props.temp_or_mode]);
    const [opened_temp_dialog, closeTempDialog, openTempDialog] = useBooleanState(false);
    const [opened_dialog, closeDialog, openDialog] = useBooleanState(false);
    const options = useMemo(() => [
        ...heating_modes
            .filter(({ name }) => name !== 'Расписание')
            .map(({ id, name }) => ({ text: name, value: id })),
    ], [heating_modes]);
    const onChangeMode = useCallback((hm_id) => {
        setSelectedHmId(hm_id);
        setTempOrMode(getTempOrMode(hm_id, heating_modes));
    }, [setSelectedHmId, setTempOrMode, getTempOrMode, heating_modes]);
    const handleRadioChange = useCallback((event) => {
        const val = event.target.value;
        setRadioModeOrTemp(val);
        if (val === 'temp') {
            setTempOrMode(getTempOrMode(temp_or_mode.temp ?? 20, heating_modes, false));
        }
        if (val === 'mode') {
            if (selected_hm_id)
                setTempOrMode(getTempOrMode(selected_hm_id, heating_modes));
        }
    }, [setRadioModeOrTemp, getTempOrMode, heating_modes, setTempOrMode, temp_or_mode]);
    // const boiler_options = useMemo(
    //     () => [
    //         ...HeatingMode.special_modes.map((mode) => ({
    //             type: 'temp',
    //             id: mode,
    //             label: HeatingMode.getSpecialModeName(mode),
    //         })),
    //         ...heating_modes.map((el) => ({
    //             type: 'mode',
    //             id: el.id as number,
    //             label: el.name,
    //         })),
    //     ],
    //     [heating_modes]
    // );
    const handleSpecialChange = useCallback((event) => {
        const val = +event.target.value;
        const mode_index = heating_modes.findIndex((el) => el.id === val);
        setBoilerSelectedValue(val);
        if (mode_index >= 0) {
            setTempOrMode(getTempOrMode(val, heating_modes));
        }
        else {
            setTempOrMode({ temp: val, mode: undefined });
        }
    }, [setRadioModeOrTemp, getTempOrMode, heating_modes, setTempOrMode, temp_or_mode]);
    const onChangeTemp = useCallback((temp) => {
        if (temp > 130 || temp < -20) {
            return;
        }
        setTempOrMode(getTempOrMode(temp, heating_modes, false));
    }, [setTempOrMode, getTempOrMode, heating_modes]);
    const onChangeInDialog = useCallback((temp) => {
        onChangeTemp(temp);
        closeTempDialog();
    }, [onChangeTemp, closeTempDialog]);
    const onApply = useCallback(() => {
        onChange(temp_or_mode);
        closeDialog();
    }, [onChange, closeDialog, temp_or_mode]);
    const onKeyDown = useCallback((ev) => {
        if (ev.key == 'Enter') {
            preventDefault(ev);
            onApply();
        }
    }, [onApply]);
    const mode_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: options })), [options]);
    const temp_field = useCallback((input_props) => (_jsx(TempstepField, { ...input_props, startAdornment: _jsx(InputAdornment, { position: "start", children: "\u00B0C" }), endAdornment: _jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { className: classes.button_to_right, size: "small", onClick: openTempDialog, children: _jsx(ArrowDropDown, {}) }) }), onKeyDown: onKeyDown })), [classes, onKeyDown]);
    const field_options = useMemo(() => ({
        readOnly: true,
        startAdornment: temp_or_mode.temp != null && !is_boiler_mode ? (_jsx(InputAdornment, { position: "start", children: "\u00B0C" })) : undefined,
        endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(IconButton, { className: classes.button_to_right, size: "small", disabled: disabled, onClick: openDialog, children: _jsx(ArrowDropDown, {}) }) })),
    }), [temp_or_mode, classes, disabled, openDialog]);
    return (_jsxs(Root, { children: [_jsx(TextField, { variant: "outlined", type: "text", size: "small", fullWidth: true, disabled: disabled, value: temp_or_mode.temp != null
                    ? HeatingMode.special_modes.includes(temp_or_mode.temp)
                        ? HeatingMode.getSpecialModeName(temp_or_mode.temp)
                        : prettyFloat(temp_or_mode.temp)
                    : temp_or_mode.mode?.name, onClick: disabled ? undefined : openDialog, InputProps: field_options }), _jsxs(Dialog, { open: opened_dialog, onClose: closeDialog, children: [_jsx(DialogTitle, { children: _jsxs(Box, { display: "flex", alignItems: "baseline", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h6", children: `Выбор ${is_boiler_mode || radio_mode_or_temp === 'mode'
                                        ? 'режима'
                                        : 'температуры'}` }), _jsx(Box, { ml: 1, mr: -2, children: _jsx(IconButton, { onClick: closeDialog, size: "large", children: _jsx(Close, {}) }) })] }) }), _jsx(DialogContent, { children: _jsxs(Grid, { container: true, spacing: 2, children: [!is_boiler_mode && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 12, children: _jsxs(RadioGroup, { value: radio_mode_or_temp, onChange: handleRadioChange, children: [_jsx(FormControlLabel, { value: "mode", control: _jsx(Radio, { color: "primary" }), label: "\u0420\u0435\u0436\u0438\u043C" }), _jsx(FormControlLabel, { value: "temp", control: _jsx(Radio, { color: "primary" }), label: "\u0422\u0435\u043C\u043F\u0435\u0440\u0430\u0442\u0443\u0440\u0430" })] }) }), radio_mode_or_temp === 'mode' && (_jsx(Grid, { item: true, xs: 12, children: _jsx(ControlWithoutLabel, { onChange: onChangeMode, value: selected_hm_id, field: mode_field, validation_result: null, validation_warnings_result: null }) })), radio_mode_or_temp === 'temp' && (_jsx(Grid, { item: true, xs: 12, children: _jsx(ControlWithoutLabel, { value: temp_or_mode.temp ?? 20, onChange: onChangeTemp, validation_result: null, validation_warnings_result: null, field: temp_field }) }))] })), is_boiler_mode && (_jsx(Grid, { item: true, xs: 12, children: _jsx(RadioGroup, { value: temp_or_mode.temp ?? HeatingMode.ALWAYS_ON_MODE, onChange: handleSpecialChange, children: HeatingMode.special_modes.map((mode) => (_jsx(FormControlLabel, { value: mode, control: _jsx(Radio, { color: "primary" }), label: HeatingMode.getSpecialModeName(mode) }, mode))) }) }))] }) }), _jsx(DialogActions, { children: _jsx(Button, { onClick: onApply, children: "ok" }) })] }), _jsxs(Dialog, { open: opened_temp_dialog, onClose: closeTempDialog, children: [_jsx(DialogTitle, { children: _jsxs(Box, { display: "flex", alignItems: "baseline", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h6", children: "\u0412\u044B\u0431\u043E\u0440 \u0442\u0435\u043C\u043F\u0435\u0440\u0430\u0442\u0443\u0440\u044B" }), _jsx(Box, { ml: 1, mr: -2, children: _jsx(IconButton, { onClick: closeTempDialog, size: "large", children: _jsx(Close, {}) }) })] }) }), _jsx(TemperaturePicker, { onChange: onChangeInDialog })] })] }));
});
